// Hamburger menu toggle functionality
document.addEventListener('DOMContentLoaded', () => {
  const menuToggle = document.querySelector('.menu-toggle');
  const navMenu = document.querySelector('.nav-menu');
  const navOverlay = document.querySelector('.nav-overlay');
  const navLinks = document.querySelectorAll('.nav-menu a');
  let focusableElements = [];
  let firstFocusableElement = null;
  let lastFocusableElement = null;

  // Get all focusable elements within the menu
  function getFocusableElements() {
    const focusableSelectors =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(navMenu.querySelectorAll(focusableSelectors));
  }

  // Trap focus within menu when open
  function trapFocus(e) {
    if (!navMenu.classList.contains('active')) return;

    focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    firstFocusableElement = focusableElements[0];
    lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusableElement) {
          e.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusableElement) {
          e.preventDefault();
          firstFocusableElement.focus();
        }
      }
    }
  }

  // Toggle menu function
  function toggleMenu() {
    const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true';
    menuToggle.setAttribute('aria-expanded', !isExpanded);
    navMenu.classList.toggle('active');
    navOverlay.classList.toggle('active');
    document.body.style.overflow = !isExpanded ? 'hidden' : '';

    // Focus management
    if (!isExpanded) {
      // Opening menu - focus first link
      focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        setTimeout(() => focusableElements[0].focus(), 100);
      }
      navMenu.addEventListener('keydown', trapFocus);
    } else {
      // Closing menu - restore focus to toggle button
      menuToggle.focus();
      navMenu.removeEventListener('keydown', trapFocus);
    }
  }

  // Toggle menu on button click
  menuToggle.addEventListener('click', toggleMenu);

  // Close menu when clicking overlay
  navOverlay.addEventListener('click', toggleMenu);

  // Close menu on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && navMenu.classList.contains('active')) {
      toggleMenu();
    }
  });

  // Close menu when clicking a link
  navLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      // Handle anchor links
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);

        if (targetId === 'home') {
          // Scroll to top for home
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else {
          const targetElement = document.getElementById(targetId);

          if (targetElement) {
            const headerOffset = 80;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });

            // Update active nav link if it's a main section
            if (targetElement.tagName === 'SECTION') {
              navLinks.forEach((navLink) => {
                navLink.classList.remove('active');
                navLink.removeAttribute('aria-current');
                if (navLink.getAttribute('href') === href) {
                  navLink.classList.add('active');
                  navLink.setAttribute('aria-current', 'page');
                }
              });
            } else {
              // For subsections, activate the parent section (Services)
              navLinks.forEach((navLink) => {
                navLink.classList.remove('active');
                navLink.removeAttribute('aria-current');
                if (navLink.getAttribute('href') === '#services') {
                  navLink.classList.add('active');
                  navLink.setAttribute('aria-current', 'page');
                }
              });
            }
          }
        }
      }

      // Close menu on mobile after navigation
      if (window.innerWidth <= 768) {
        toggleMenu();
      }
    });
  });

  // Close menu on window resize if it's larger than mobile
  window.addEventListener('resize', () => {
    if (window.innerWidth > 768) {
      menuToggle.setAttribute('aria-expanded', 'false');
      navMenu.classList.remove('active');
      navOverlay.classList.remove('active');
      document.body.style.overflow = '';
      navMenu.removeEventListener('keydown', trapFocus);
    }
  });

  // Initialize carousels
  initCarousel();
  initBenefitsCarousel();
  initTestimonialsCarousel();

  // Update active nav link on scroll
  updateActiveNavLink();
  window.addEventListener('scroll', updateActiveNavLink);
});

// Update active navigation link based on scroll position
function updateActiveNavLink() {
  const sections = document.querySelectorAll('section[id]');
  const subsections = document.querySelectorAll('.service-subsection[id]');
  const navLinks = document.querySelectorAll('.nav-menu a');
  const scrollPosition = window.pageYOffset + 100;

  // If at the top, set home as active
  if (window.pageYOffset < 100) {
    navLinks.forEach((link) => {
      link.classList.remove('active');
      link.removeAttribute('aria-current');
      if (link.getAttribute('href') === '#home') {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      }
    });
    return;
  }

  // Check subsections first (they're within Services section)
  let activeSubsection = null;
  subsections.forEach((subsection) => {
    const subsectionTop = subsection.offsetTop;
    const subsectionHeight = subsection.offsetHeight;
    const subsectionId = subsection.getAttribute('id');

    if (
      scrollPosition >= subsectionTop &&
      scrollPosition < subsectionTop + subsectionHeight
    ) {
      activeSubsection = subsectionId;
    }
  });

  // If in a subsection, activate Services
  if (activeSubsection) {
    navLinks.forEach((link) => {
      link.classList.remove('active');
      link.removeAttribute('aria-current');
      if (link.getAttribute('href') === '#services') {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      }
    });
    return;
  }

  // Check main sections
  sections.forEach((section) => {
    const sectionTop = section.offsetTop;
    const sectionHeight = section.offsetHeight;
    const sectionId = section.getAttribute('id');

    if (
      scrollPosition >= sectionTop &&
      scrollPosition < sectionTop + sectionHeight
    ) {
      navLinks.forEach((link) => {
        link.classList.remove('active');
        link.removeAttribute('aria-current');
        if (link.getAttribute('href') === `#${sectionId}`) {
          link.classList.add('active');
          link.setAttribute('aria-current', 'page');
        }
      });
    }
  });
}

// Carousel functionality
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Reusable touch/swipe handler for carousels
function setupTouchHandling(
  carousel,
  nextSlide,
  prevSlide,
  stopAutoPlay,
  startAutoPlay
) {
  let touchStartX = 0;
  let touchEndX = 0;

  carousel.addEventListener(
    'touchstart',
    (e) => {
      touchStartX = e.changedTouches[0].screenX;
      stopAutoPlay();
    },
    { passive: true }
  );

  carousel.addEventListener(
    'touchend',
    (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      startAutoPlay();
    },
    { passive: true }
  );
}

function initCarousel() {
  const carousel = document.querySelector('.carousel');
  if (!carousel) return;

  const slides = carousel.querySelectorAll('.carousel-slide');
  const indicators = carousel.querySelectorAll('.carousel-indicator');
  const prevBtn = carousel.querySelector('.carousel-btn-prev');
  const nextBtn = carousel.querySelector('.carousel-btn-next');

  let currentSlide = 0;
  let autoPlayInterval = null;
  const autoPlayDelay = 5000; // 5 seconds

  function showSlide(index) {
    // Remove active class from all slides and indicators
    slides.forEach((slide, i) => {
      slide.classList.remove('active');
      slide.setAttribute('aria-hidden', 'true');
    });
    indicators.forEach((indicator) => {
      indicator.classList.remove('active');
      indicator.setAttribute('aria-selected', 'false');
    });

    // Add active class to current slide and indicator
    slides[index].classList.add('active');
    slides[index].setAttribute('aria-hidden', 'false');
    indicators[index].classList.add('active');
    indicators[index].setAttribute('aria-selected', 'true');

    currentSlide = index;

    // Announce slide change to screen readers (dedicated sr-only live region only)
    const slideText = slides[index].textContent.trim().substring(0, 100);
    const liveRegion = document.getElementById('hero-carousel-live');
    if (liveRegion) {
      liveRegion.textContent = `Slide ${index + 1} of ${
        slides.length
      }: ${slideText}`;
    }
  }

  function nextSlide() {
    const next = (currentSlide + 1) % slides.length;
    showSlide(next);
  }

  function prevSlide() {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    showSlide(prev);
  }

  function goToSlide(index) {
    if (index >= 0 && index < slides.length) {
      showSlide(index);
    }
  }

  function startAutoPlay() {
    autoPlayInterval = setInterval(nextSlide, autoPlayDelay);
  }

  function stopAutoPlay() {
    if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
    }
  }

  // Helper to reset autoplay
  const resetAutoPlay = () => {
    stopAutoPlay();
    startAutoPlay();
  };

  // Event listeners
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      nextSlide();
      resetAutoPlay();
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      prevSlide();
      resetAutoPlay();
    });
  }

  // Indicator clicks
  indicators.forEach((indicator, index) => {
    indicator.addEventListener('click', () => {
      goToSlide(index);
      resetAutoPlay();
    });
  });

  // Touch events for mobile swipe
  setupTouchHandling(
    carousel,
    nextSlide,
    prevSlide,
    stopAutoPlay,
    startAutoPlay
  );

  // Keyboard navigation
  carousel.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
      resetAutoPlay();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
      resetAutoPlay();
    }
  });

  // Pause on hover
  carousel.addEventListener('mouseenter', stopAutoPlay);
  carousel.addEventListener('mouseleave', startAutoPlay);

  // Make carousel focusable for keyboard navigation
  carousel.setAttribute('tabindex', '0');

  // Initialize aria-hidden attributes
  slides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
  });

  // Start auto-play (respects prefers-reduced-motion)
  startAutoPlay();
}

// Benefits Carousel functionality
function initBenefitsCarousel() {
  const carousel = document.querySelector('.benefits-carousel');
  if (!carousel) return;

  const track = carousel.querySelector('.benefits-carousel-track');
  const slides = carousel.querySelectorAll('.benefits-slide');
  const indicators = carousel.querySelectorAll('.benefits-carousel-indicator');
  const prevBtn = carousel.querySelector('.benefits-carousel-btn-prev');
  const nextBtn = carousel.querySelector('.benefits-carousel-btn-next');
  const viewport = carousel.querySelector('.benefits-carousel-viewport');

  let currentIndex = 1; // Start with second card (index 1) as center
  let autoPlayInterval = null;
  const autoPlayDelay = 5000; // 5 seconds
  let cardsToShow = 3; // Default: show 3 cards

  // Calculate how many cards to show based on screen size
  function getCardsToShow() {
    if (window.innerWidth <= 768) {
      return 1;
    } else if (window.innerWidth <= 1024) {
      return 2;
    }
    return 3;
  }

  // Update cards to show on resize
  function updateCardsToShow() {
    cardsToShow = getCardsToShow();
    updateCarousel();
  }

  window.addEventListener('resize', updateCardsToShow);
  cardsToShow = getCardsToShow();

  function updateCarousel() {
    // Ensure currentIndex is within bounds
    if (currentIndex < 0) currentIndex = 0;
    if (currentIndex >= slides.length) currentIndex = slides.length - 1;

    // Calculate which cards to show (center the active one)
    const centerOffset = Math.floor(cardsToShow / 2);
    let startIndex = currentIndex - centerOffset;

    // Adjust startIndex to stay within bounds
    if (startIndex < 0) startIndex = 0;
    if (startIndex + cardsToShow > slides.length) {
      startIndex = Math.max(0, slides.length - cardsToShow);
    }

    // Calculate translateX
    // Each slide is flex: 0 0 calc(33.333% - 1.33rem) for 3 cards
    // Gap is 2rem (or 1.5rem for 2 cards, 1rem for 1 card)
    const gap = cardsToShow === 3 ? 2 : cardsToShow === 2 ? 1.5 : 1;
    const gapRem = gap;
    const viewportWidth = viewport.offsetWidth;
    const gapPx = gapRem * 16; // Convert rem to px (assuming 16px base)
    const slideWidthPx =
      (viewportWidth - gapPx * (cardsToShow - 1)) / cardsToShow;
    const totalSlideWidthPx = slideWidthPx + gapPx;

    const translateX = -(startIndex * totalSlideWidthPx);
    track.style.transform = `translateX(${translateX}px)`;

    // Update active states
    slides.forEach((slide, index) => {
      slide.classList.remove('active');
      slide.setAttribute('aria-hidden', 'true');
      if (index === currentIndex) {
        slide.classList.add('active');
        slide.setAttribute('aria-hidden', 'false');
      }
    });

    // Announce slide change to screen readers (dedicated sr-only live region only)
    const activeSlide = slides[currentIndex];
    if (activeSlide) {
      const slideText = activeSlide.textContent.trim().substring(0, 100);
      const liveRegion = document.getElementById('benefits-carousel-live');
      if (liveRegion) {
        liveRegion.textContent = `Benefits carousel: ${slideText}`;
      }
    }

    // Update indicators (group by center positions: 1, 4, 7)
    const centerIndices = [1, 4, 7];
    let activeIndicatorIndex = 1; // Default to middle group

    if (currentIndex <= 2) {
      activeIndicatorIndex = 0; // First group (cards 0-2, center: 1)
    } else if (currentIndex >= 3 && currentIndex <= 5) {
      activeIndicatorIndex = 1; // Second group (cards 3-5, center: 4)
    } else if (currentIndex >= 6) {
      activeIndicatorIndex = 2; // Third group (cards 6-8, center: 7)
    }

    indicators.forEach((indicator, index) => {
      indicator.classList.remove('active');
      indicator.setAttribute('aria-selected', 'false');
      if (index === activeIndicatorIndex) {
        indicator.classList.add('active');
        indicator.setAttribute('aria-selected', 'true');
      }
    });
  }

  function nextSlide() {
    if (currentIndex < slides.length - 1) {
      currentIndex++;
      updateCarousel();
    } else {
      // Loop back to start
      currentIndex = 0;
      updateCarousel();
    }
  }

  function prevSlide() {
    if (currentIndex > 0) {
      currentIndex--;
      updateCarousel();
    } else {
      // Loop to end
      currentIndex = slides.length - 1;
      updateCarousel();
    }
  }

  function goToSlide(groupIndex) {
    // Each indicator represents a group of cards
    // With 9 cards and 3 cards shown, we have 3 groups: 0-2, 3-5, 6-8
    // Center card for each group: 1, 4, 7
    const centerIndices = [1, 4, 7];
    if (groupIndex >= 0 && groupIndex < centerIndices.length) {
      currentIndex = centerIndices[groupIndex];
      updateCarousel();
    }
  }

  function startAutoPlay() {
    if (prefersReducedMotion()) return;
    autoPlayInterval = setInterval(nextSlide, autoPlayDelay);
  }

  function stopAutoPlay() {
    if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
    }
  }

  // Helper to reset autoplay
  const resetAutoPlay = () => {
    stopAutoPlay();
    startAutoPlay();
  };

  // Event listeners
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      nextSlide();
      resetAutoPlay();
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      prevSlide();
      resetAutoPlay();
    });
  }

  // Indicator clicks
  indicators.forEach((indicator, index) => {
    indicator.addEventListener('click', () => {
      goToSlide(index);
      resetAutoPlay();
    });
  });

  // Touch events for mobile swipe
  setupTouchHandling(
    carousel,
    nextSlide,
    prevSlide,
    stopAutoPlay,
    startAutoPlay
  );

  // Keyboard navigation
  carousel.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
      resetAutoPlay();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
      resetAutoPlay();
    }
  });

  // Pause on hover
  carousel.addEventListener('mouseenter', stopAutoPlay);
  carousel.addEventListener('mouseleave', startAutoPlay);

  // Make carousel focusable for keyboard navigation
  carousel.setAttribute('tabindex', '0');

  // Initialize aria-hidden attributes
  slides.forEach((slide, index) => {
    slide.setAttribute(
      'aria-hidden',
      index === currentIndex ? 'false' : 'true'
    );
  });

  // Initialize carousel
  updateCarousel();

  // Start auto-play (respects prefers-reduced-motion)
  startAutoPlay();
}

// Simple testimonials carousel
function initTestimonialsCarousel() {
  const carousel = document.querySelector('.testimonials-carousel');
  if (!carousel) return;

  const slides = carousel.querySelectorAll('.testimonial-slide');
  const indicators = carousel.querySelectorAll('.testimonial-indicator');
  const prevBtn = carousel.querySelector('.testimonial-btn-prev');
  const nextBtn = carousel.querySelector('.testimonial-btn-next');

  let currentSlide = 0;
  let autoPlayInterval = null;

  function showSlide(index) {
    // Remove active class from all slides and indicators
    slides.forEach((slide, i) => {
      slide.classList.remove('active');
      slide.setAttribute('aria-hidden', 'true');
      indicators[i].classList.remove('active');
      indicators[i].setAttribute('aria-selected', 'false');
    });

    // Add active class to current slide and indicator
    slides[index].classList.add('active');
    slides[index].setAttribute('aria-hidden', 'false');
    indicators[index].classList.add('active');
    indicators[index].setAttribute('aria-selected', 'true');
    currentSlide = index;

    // Announce slide change to screen readers (dedicated sr-only live region only)
    const slideText = slides[index].textContent.trim().substring(0, 100);
    const liveRegion = document.getElementById('testimonials-carousel-live');
    if (liveRegion) {
      liveRegion.textContent = `Testimonial ${index + 1} of ${
        slides.length
      }: ${slideText}`;
    }
  }

  function nextSlide() {
    const next = (currentSlide + 1) % slides.length;
    showSlide(next);
  }

  function prevSlide() {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    showSlide(prev);
  }

  function goToSlide(index) {
    showSlide(index);
  }

  function startAutoPlay() {
    if (prefersReducedMotion()) return;
    autoPlayInterval = setInterval(nextSlide, 5000);
  }

  function stopAutoPlay() {
    if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
    }
  }

  // Helper to reset autoplay
  const resetAutoPlay = () => {
    stopAutoPlay();
    startAutoPlay();
  };

  // Event listeners
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      nextSlide();
      resetAutoPlay();
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      prevSlide();
      resetAutoPlay();
    });
  }

  indicators.forEach((indicator, index) => {
    indicator.addEventListener('click', () => {
      goToSlide(index);
      resetAutoPlay();
    });
  });

  // Keyboard navigation
  carousel.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      prevSlide();
      resetAutoPlay();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
      resetAutoPlay();
    }
  });

  // Pause on hover
  carousel.addEventListener('mouseenter', stopAutoPlay);
  carousel.addEventListener('mouseleave', startAutoPlay);

  // Make carousel focusable for keyboard navigation
  carousel.setAttribute('tabindex', '0');

  // Initialize aria-hidden attributes
  slides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
  });

  // Initialize carousel
  showSlide(0);

  // Start auto-play (respects prefers-reduced-motion)
  startAutoPlay();
}
